import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Spinner from '../../components/Spinner'
import { getOrder, submitPayment, uploadReceipt } from '../../api/orders'
import { getSettings } from '../../api/settings'
import { formatMoney, formatDate } from '../../utils/format'
import { useAuth } from '../../context/AuthContext'
import { useTelegram } from '../../hooks/useTelegram'

const G = '#C89B4C'
const GB = 'rgba(200,155,76,0.14)'
const BD = 'rgba(244,238,225,0.14)'
const S = 'linear-gradient(155deg, rgba(255,248,235,0.05) 0%, rgba(255,248,235,0.015) 100%)'
const GLASS = { background: S, backdropFilter: 'blur(11px) saturate(150%)', WebkitBackdropFilter: 'blur(11px) saturate(150%)' } as const
const TX = '#F4F1EA'
const TX2 = '#C8C4BB'
const TX3 = '#8A8480'

function BackBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 6, color: TX3, fontSize: 13, background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginBottom: 20 }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      {label}
    </button>
  )
}

function InfoRow({ label, value, hi }: { label: string; value: string; hi?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${BD}` }}>
      <span style={{ fontSize: 12, color: TX3 }}>{label}</span>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: hi ? 16 : 13, color: hi ? G : TX, fontWeight: hi ? 600 : 400 }}>{value}</span>
    </div>
  )
}

export default function SubmitPaymentPage() {
  const { orderId, paymentId } = useParams<{ orderId: string; paymentId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { tg } = useTelegram()
  const lang = user?.language ?? 'ru'

  const [method, setMethod] = useState<'cash' | 'transfer' | null>(null)
  const [amountUzs, setAmountUzs] = useState('')
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(Number(orderId)),
    enabled: !!orderId,
  })
  const { data: settings } = useQuery({ queryKey: ['settings-public'], queryFn: getSettings, retry: false })

  const payment    = order?.payments?.find(p => p.id === Number(paymentId))
  const usdRate    = settings?.usd_rate ?? 12000
  const amountDue  = payment
    ? (payment.status === 'partially_paid' ? payment.amount - payment.paid_amount : payment.amount)
    : 0

  const unpaidPayments = (order?.payments ?? [])
    .filter(p => p.status === 'pending' || p.status === 'overdue' || p.status === 'partially_paid')
    .sort((a, b) => a.payment_num - b.payment_num)

  const rawUzs = Number(amountUzs.replace(/\s/g, ''))
  const paidUsd = rawUzs > 0 ? rawUzs / usdRate : 0

  function getCoverage(usd: number) {
    let remaining = usd
    const covered: number[] = []
    for (const p of unpaidPayments) {
      if (remaining <= 0) break
      const needed = p.amount - (p.status === 'partially_paid' ? p.paid_amount : 0)
      if (remaining >= needed) { covered.push(p.payment_num); remaining -= needed }
      else return { covered, partialNum: p.payment_num, partialLeft: needed - remaining }
    }
    return { covered, partialNum: null, partialLeft: 0 }
  }

  const coverage = paidUsd > 0 ? getCoverage(paidUsd) : null

  useEffect(() => {
    tg?.BackButton.show()
    const cb = () => navigate(-1)
    tg?.BackButton.onClick(cb)
    return () => { tg?.BackButton.offClick(cb); tg?.BackButton.hide() }
  }, []) // eslint-disable-line

  const submitMut = useMutation({
    mutationFn: async () => {
      let receipt_url: string | undefined
      if (receiptFile) receipt_url = await uploadReceipt(receiptFile)
      return submitPayment(Number(orderId), Number(paymentId), {
        method: method!,
        receipt_url,
        paid_amount_uzs: amountUzs ? Number(amountUzs.replace(/\s/g, '')) : undefined,
      })
    },
    onSuccess: () => { tg?.HapticFeedback.notificationOccurred('success'); setDone(true) },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? (lang === 'uz' ? 'Xatolik' : 'Ошибка')),
  })

  function copyCard() {
    if (settings?.card_number) {
      navigator.clipboard.writeText(settings.card_number)
      toast.success(lang === 'uz' ? 'Karta raqami nusxalandi' : 'Номер карты скопирован')
      tg?.HapticFeedback.impactOccurred('light')
    }
  }

  function pickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    setReceiptFile(f)
    setReceiptPreview(URL.createObjectURL(f))
  }

  if (isLoading) return <Spinner className="h-screen" />
  if (!payment) return null

  const pageStyle: React.CSSProperties = { minHeight: '100vh', display: 'flex', flexDirection: 'column' }
  const headerStyle: React.CSSProperties = {
    position: 'sticky', top: 0, zIndex: 40, padding: '10px 20px',
    background: 'rgba(13,21,38,0.82)', backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)', borderBottom: `1px solid ${BD}`,
    display: 'flex', alignItems: 'center', gap: 12,
  }
  const mainStyle: React.CSSProperties = { flex: 1, padding: '20px 20px 40px', overflowY: 'auto' }

  // ── Success ──
  if (done) {
    return (
      <div style={pageStyle}>
        <header style={headerStyle}>
          <span style={{ fontSize: 11, fontWeight: 600, color: TX3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {lang === 'uz' ? "To'lov" : 'Платёж'}
          </span>
        </header>
        <main style={{ ...mainStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, textAlign: 'center' }}>
          <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(76,175,126,0.15)', border: '1.5px solid rgba(76,175,126,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4CAF7E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: TX, margin: '0 0 8px' }}>
              {method === 'cash'
                ? (lang === 'uz' ? "To'lov ro'yxatdan o'tdi!" : 'Платёж зарегистрирован!')
                : (lang === 'uz' ? 'Tasdiqlanishga yuborildi!' : 'Отправлено на подтверждение!')}
            </h2>
            <p style={{ fontSize: 13, color: TX3, margin: 0 }}>
              {method === 'cash'
                ? (lang === 'uz' ? "Menejer naqd olganidan keyin tasdiqlaydi" : 'Администратор подтвердит платёж после получения наличных.')
                : (lang === 'uz' ? "Menejer chekni tekshiradi va tasdiqlaydi" : 'Администратор проверит чек и подтвердит оплату.')}
            </p>
          </div>
          <button onClick={() => navigate(-1)} style={{ marginTop: 8, padding: '12px 28px', borderRadius: 12, background: GB, border: `1.5px solid ${G}`, color: G, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {lang === 'uz' ? 'Orqaga' : 'Назад'}
          </button>
        </main>
      </div>
    )
  }

  // ── Method selection ──
  if (!method) {
    return (
      <div style={pageStyle}>
        <header style={headerStyle}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: TX3, cursor: 'pointer', padding: 0, display: 'flex' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <span style={{ fontSize: 11, fontWeight: 600, color: TX3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {lang === 'uz' ? "To'lov" : 'Платёж'} #{payment.payment_num}
          </span>
        </header>
        <main style={mainStyle}>
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 30, color: TX, margin: '0 0 4px' }}>{formatMoney(amountDue)}</p>
          {payment.status === 'partially_paid' && (
            <p style={{ fontSize: 12, color: G, margin: '0 0 4px' }}>
              {lang === 'uz' ? "Qisman to'langan" : 'Частично оплачен'}: {formatMoney(payment.paid_amount)} — {lang === 'uz' ? 'Qoldiq' : 'остаток'} {formatMoney(amountDue)}
            </p>
          )}
          <p style={{ fontSize: 13, color: TX3, margin: '0 0 28px' }}>
            {lang === 'uz' ? 'Muddati' : 'Срок'}: {formatDate(payment.due_date)}
          </p>

          {unpaidPayments.some(p => p.payment_num < payment.payment_num && p.status === 'overdue') && (
            <div style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(224,94,94,0.08)', border: '1px solid rgba(224,94,94,0.20)', marginBottom: 20 }}>
              <p style={{ fontSize: 12, color: '#E05E5E', margin: 0 }}>
                ⚠️ {lang === 'uz' ? "Kechiktirilgan to'lovlar mavjud" : 'Есть просроченные платежи. Сумма к оплате больше.'}
              </p>
            </div>
          )}

          <p style={{ fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 12 }}>
            {lang === 'uz' ? "TO'LOV USULI" : 'СПОСОБ ОПЛАТЫ'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { key: 'transfer' as const, title: lang === 'uz' ? '🏦 Karta orqali' : '🏦 Картой (перевод)', sub: lang === 'uz' ? "Karta raqamiga o'tkazing, chekni yuboring" : 'Переведите на карту, отправьте скриншот чека' },
              { key: 'cash'     as const, title: lang === 'uz' ? '💵 Naqd pul'      : '💵 Наличными',         sub: lang === 'uz' ? "Ofisga keling yoki kuryer orqali"          : 'Принесите наличные в офис или через курьера' },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setMethod(opt.key)}
                style={{
                  padding: '16px 18px', borderRadius: 14, textAlign: 'left',
                  ...GLASS, border: `1px solid ${BD}`, cursor: 'pointer',
                }}
              >
                <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: TX }}>{opt.title}</p>
                <p style={{ margin: 0, fontSize: 12, color: TX3 }}>{opt.sub}</p>
              </button>
            ))}
          </div>
        </main>
      </div>
    )
  }

  // ── UZS amount + coverage block (shared) ──
  const uzsBlock = (
    <div style={{ padding: 16, borderRadius: 14, ...GLASS, border: `1px solid ${BD}`, marginBottom: 16 }}>
      <p style={{ margin: '0 0 10px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
        {lang === 'uz' ? "TO'LOV SUMMASI (so'mda)" : 'СУММА ОПЛАТЫ (в сумах)'}
      </p>
      <input
        className="input"
        inputMode="numeric"
        placeholder="0"
        value={amountUzs}
        style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20 }}
        onChange={e => {
          const digits = e.target.value.replace(/\D/g, '')
          setAmountUzs(digits ? Number(digits).toLocaleString('ru-RU') : '')
        }}
      />
      {coverage && (
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: 'rgba(244,238,225,0.04)' }}>
          <p style={{ margin: '0 0 4px', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: TX3 }}>
            ≈ <span style={{ color: TX }}>${paidUsd.toFixed(0)}</span>
          </p>
          {coverage.covered.length > 0 && (
            <p style={{ margin: '0 0 2px', fontSize: 11, color: TX3 }}>
              ✅ {lang === 'uz' ? "Oylarni yopadi" : 'Закроет месяц'}{coverage.covered.length > 1 ? (lang === 'uz' ? 'ni' : 'ы') : ''}: <span style={{ color: TX }}>{coverage.covered.map(n => `#${n}`).join(', ')}</span>
            </p>
          )}
          {coverage.partialNum && (
            <p style={{ margin: '0 0 2px', fontSize: 11, color: TX3 }}>
              ⚠️ #{coverage.partialNum} {lang === 'uz' ? "qisman" : 'частично'} — {lang === 'uz' ? 'qoldiq' : 'останется'} <span style={{ color: TX }}>{formatMoney(coverage.partialLeft)}</span>
            </p>
          )}
          {coverage.covered.length === 0 && !coverage.partialNum && (
            <p style={{ margin: 0, fontSize: 11, color: '#E05E5E' }}>{lang === 'uz' ? "Summa minimal to'lovdan kam" : 'Сумма меньше минимального платежа'}</p>
          )}
        </div>
      )}
    </div>
  )

  // ── Cash ──
  if (method === 'cash') {
    return (
      <div style={pageStyle}>
        <header style={headerStyle}>
          <button onClick={() => setMethod(null)} style={{ background: 'none', border: 'none', color: TX3, cursor: 'pointer', padding: 0, display: 'flex' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <span style={{ fontSize: 11, fontWeight: 600, color: TX3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {lang === 'uz' ? '💵 Naqd pul' : '💵 Наличными'}
          </span>
        </header>
        <main style={mainStyle}>
          <div style={{ padding: 16, borderRadius: 14, ...GLASS, border: `1px solid ${BD}`, marginBottom: 16 }}>
            <InfoRow label={lang === 'uz' ? "To'lov miqdori" : 'Сумма к оплате'} value={formatMoney(amountDue)} hi />
            <InfoRow label="≈ сум" value={`${Math.round(amountDue * usdRate).toLocaleString('ru-RU')} сум`} />
            <InfoRow label={lang === 'uz' ? 'Muddati' : 'Срок оплаты'} value={formatDate(payment.due_date)} />
            <div style={{ padding: '10px 0' }}>
              <span style={{ fontSize: 12, color: TX3 }}>{lang === 'uz' ? 'Ariza' : 'Заявка'}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", float: 'right', fontSize: 13, color: TX }}>{order?.registry_num}</span>
            </div>
          </div>
          {uzsBlock}
          <div style={{ padding: 14, borderRadius: 14, ...GLASS, border: `1px solid ${BD}`, marginBottom: 20 }}>
            <p style={{ fontSize: 13, color: TX2, margin: '0 0 6px' }}>
              {lang === 'uz' ? 'Naqd pulni ofisga olib keling yoki kuryerga bering.' : 'Принесите наличные в офис или отдайте курьеру.'}
            </p>
            <p style={{ fontSize: 12, color: TX3, margin: 0 }}>
              {lang === 'uz' ? 'Ariza raqamini ayting:' : 'Укажите номер заявки:'} <span style={{ color: TX }}>{order?.registry_num}</span>
            </p>
          </div>
          <button
            onClick={() => submitMut.mutate()}
            disabled={submitMut.isPending}
            style={{ width: '100%', padding: '14px 0', borderRadius: 14, fontSize: 14, fontWeight: 600, background: GB, border: `1.5px solid ${G}`, color: G, cursor: 'pointer', opacity: submitMut.isPending ? 0.5 : 1 }}
          >
            {submitMut.isPending ? '...' : (lang === 'uz' ? "Menejerga xabar berish" : 'Уведомить администратора')}
          </button>
        </main>
      </div>
    )
  }

  // ── Card / transfer ──
  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <button onClick={() => setMethod(null)} style={{ background: 'none', border: 'none', color: TX3, cursor: 'pointer', padding: 0, display: 'flex' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <span style={{ fontSize: 11, fontWeight: 600, color: TX3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {lang === 'uz' ? '🏦 Karta orqali' : '🏦 Картой (перевод)'}
        </span>
      </header>
      <main style={mainStyle}>
        {/* Payment info */}
        <div style={{ padding: 16, borderRadius: 14, ...GLASS, border: `1px solid ${BD}`, marginBottom: 16 }}>
          <InfoRow label={`${lang === 'uz' ? "To'lov" : 'Платёж'} #${payment.payment_num}`} value={formatMoney(amountDue)} hi />
          {payment.status === 'partially_paid' && (
            <InfoRow label={lang === 'uz' ? "To'langan" : 'Уже оплачено'} value={formatMoney(payment.paid_amount)} />
          )}
          <InfoRow label="≈ сум" value={`${Math.round(amountDue * usdRate).toLocaleString('ru-RU')} сум`} />
          <InfoRow label={lang === 'uz' ? 'Muddati' : 'Срок оплаты'} value={formatDate(payment.due_date)} />
        </div>

        {uzsBlock}

        {/* Card details */}
        {settings?.card_number && (
          <div style={{ padding: 16, borderRadius: 14, ...GLASS, border: `1px solid ${BD}`, marginBottom: 16 }}>
            <p style={{ margin: '0 0 12px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              {lang === 'uz' ? "O'TKAZMA REKVIZITLARI" : 'РЕКВИЗИТЫ ДЛЯ ПЕРЕВОДА'}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 12, background: 'rgba(244,238,225,0.04)' }}>
              <div>
                <p style={{ margin: '0 0 2px', fontSize: 10, color: TX3 }}>{lang === 'uz' ? 'Karta raqami' : 'Номер карты'}</p>
                <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, color: TX, margin: '0 0 2px', letterSpacing: '0.12em' }}>{settings.card_number}</p>
                {settings.card_holder && <p style={{ fontSize: 11, color: TX2, margin: 0 }}>{settings.card_holder}</p>}
              </div>
              <button onClick={copyCard} style={{ background: GB, border: `1px solid ${G}`, borderRadius: 10, padding: '8px 12px', cursor: 'pointer', color: G }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
              </button>
            </div>
            <p style={{ margin: '10px 0 0', fontSize: 11, color: TX3 }}>
              {lang === 'uz' ? "Izohda ko'rsating:" : 'В комментарии укажите:'} <span style={{ color: TX }}>{order?.registry_num}</span>
            </p>
          </div>
        )}

        {/* Receipt upload (optional) */}
        <div style={{ padding: 16, borderRadius: 14, ...GLASS, border: `1px solid ${BD}`, marginBottom: 20 }}>
          <p style={{ margin: '0 0 12px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
            {lang === 'uz' ? 'CHEK RASMI (ixtiyoriy)' : 'ФОТО ЧЕКА (необязательно)'}
          </p>
          <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} style={{ display: 'none' }} />
          {receiptPreview ? (
            <div style={{ position: 'relative' }}>
              <img src={receiptPreview} alt="чек" style={{ width: '100%', borderRadius: 12, maxHeight: 200, objectFit: 'cover' }} />
              <button
                onClick={() => { setReceiptFile(null); setReceiptPreview(null) }}
                style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.65)', border: 'none', borderRadius: '50%', width: 28, height: 28, color: TX, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >×</button>
            </div>
          ) : (
            <button
              onClick={() => fileRef.current?.click()}
              style={{
                width: '100%', padding: '28px 0', borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                border: `1.5px dashed ${BD}`, background: 'none', cursor: 'pointer',
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={TX3} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
              <span style={{ fontSize: 12, color: TX3 }}>{lang === 'uz' ? 'Chek rasmini biriktirish' : 'Нажмите чтобы прикрепить скриншот'}</span>
            </button>
          )}
        </div>

        <button
          onClick={() => submitMut.mutate()}
          disabled={submitMut.isPending}
          style={{
            width: '100%', padding: '14px 0', borderRadius: 14, fontSize: 14, fontWeight: 600,
            background: GB, border: `1.5px solid ${G}`,
            color: G,
            cursor: 'pointer',
            opacity: submitMut.isPending ? 0.5 : 1, marginBottom: 8,
          }}
        >
          {submitMut.isPending ? '...' : (lang === 'uz' ? 'Tasdiqlash uchun yuborish' : 'Отправить на подтверждение')}
        </button>
      </main>
    </div>
  )
}
